import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  FileModel as FileModelV0,
  TimelineContainerModel,
} from '../../../../../../../typings/api/skymap/rest/v0/.common';
import { FileModel } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { BackendFeatureFlags } from '../../../../../../../typings/backend-feature-flags';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { iiafe } from '../../../../../utils/async';
import { FeatureFlagsManager } from '../../../../../utils/core/feature-flags-manager';
import { filenameToFontAwesomeIcon } from '../../../../../utils/io/file';
import { AssetManager } from '../../../../../viewer/asset-manager';
import { ProjectGeodataManager } from '../../../../../viewer/project-geodata-manager';
import { usePromisePool } from '../../../../hooks/use-promise-pool';
import { Project, ProjectContext } from '../../../../state/project-state/project-state';
import { getFileExtension } from '../../../../utils/path';
import { Button } from '../../../button/button';
import { CheckBox } from '../../../check-box/check-box';
import { Dialog } from '../../../dialog/dialog';
import { Icon } from '../../../icon/icon';
import { InfoBox } from '../../../info-box/info-box';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Stack } from '../../../stack/stack';
import { Table } from '../../../table/table';

type Props = {
  files: FileModel[];
  onClose: (filesAdded: boolean) => void;
};

enum ItemStatus {
  Canceled,
  Failed,
  InProgress,
  Pending,
  Success,
}

type Item = {
  file: FileModel;
  status: ItemStatus;
};
type ItemsState = Item[];
type ItemsAction =
  | { type: 'INIT_ITEMS_TO_PENDING'; items: FileModel[] }
  | { type: 'INIT_EXISTING_ITEMS_TO_PENDING' }
  | { type: 'SET_STATUS'; item: FileModel; status: ItemStatus }
  | { type: 'SET_ALL_PENDING_TO_CANCELED' };

const AddFilesToSkyViewDialog = (props: Props) => {
  const { t } = useTranslation();

  const { project } = useContext(ProjectContext);
  const [pdfPreferVector, setPdfPreferVector] = useState(false);
  const hasPdf = useMemo(
    () => props.files.some((x) => getFileExtension(x.name).toLowerCase() === '.pdf'),
    [props.files],
  );
  const [createPois, setCreatePois] = useState(false);
  const hasJpg = useMemo(
    () =>
      props.files.some((x) => ['.jpg', '.jpeg'].includes(getFileExtension(x.name).toLowerCase())),
    [props.files],
  );

  const [items, dispatchItems] = React.useReducer(itemsReducer, []);
  const itemIdsPickedByPromisePool = React.useRef<Set<string>>(new Set());
  // Counts the clicks on Add, so that a conversion of an earlier click that has been canceled and
  // retried does not start adding the files, see saveButtonClick.
  const saveAttempt = React.useRef(0);

  const anySuccess = items.some((x) => x.status === ItemStatus.Success);
  const anyFailed = items.some((x) => x.status === ItemStatus.Failed);
  const anyCanceled = items.some((x) => x.status === ItemStatus.Canceled);
  const anyPending = items.some((x) => x.status === ItemStatus.Pending);
  const anyInProgress = items.some((x) => x.status === ItemStatus.InProgress);
  const anyPendingOrInProgress = anyPending || anyInProgress;

  const closeButtonClick = React.useCallback(() => {
    props.onClose(anySuccess);
  }, [anySuccess, props]);

  const createPoiWithImage = useCallback(
    async (name: string, fileId: string) => {
      const projectServiceV1 = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
      const { data: image } = await projectServiceV1.createImage({
        path: { projectId: project.id },
        body: { fileId, name },
      });

      const imageCapturedTime = new Date(image.capturedAt).getTime();
      const closestTimeline: TimelineContainerModel | undefined = fetchClosestTimelineContainer(
        project,
        imageCapturedTime,
      );

      // Create a POI along with a comment (both represented by dynamic asset for now).
      const { data: newPoi } = await projectServiceV1.createPoi({
        body: {
          wgs84Latitude: image.wgs84Latitude ?? 0,
          wgs84Longitude: image.wgs84Longitude ?? 0,
          metersAboveMeanSeaLevel: image.metersAboveMeanSeaLevel ?? 0,
          title: name,
          timelineContainerId: closestTimeline?.id ?? 'latest',
        },
        path: {
          projectId: project.id,
        },
        query: {
          include: ['poi.createdBy', 'poi.updatedBy', 'poi.timelineContainer'],
        },
      });

      await SkyMapAxiosServiceFactory.instance.createPoiServiceV1().associateImageWithPoi({
        path: { poiId: newPoi.id, imageId: image.id },
      });
    },
    [project],
  );

  React.useEffect(() => {
    const allSuccess = items.length > 0 && items.every((x) => x.status === ItemStatus.Success);
    if (allSuccess) {
      closeButtonClick();
    }
  }, [closeButtonClick, items, props]);

  const saveButtonClick = () => {
    itemIdsPickedByPromisePool.current.clear();
    const attempt = ++saveAttempt.current;

    // The files that are added now: all of them, or the ones that failed or were canceled.
    const files =
      items.length === 0
        ? props.files
        : items
            .filter((x) => [ItemStatus.Failed, ItemStatus.Canceled].includes(x.status))
            .map((x) => x.file);

    if (items.length === 0) {
      dispatchItems({
        type: 'INIT_ITEMS_TO_PENDING',
        items: props.files,
      });
    } else {
      dispatchItems({
        type: 'INIT_EXISTING_ITEMS_TO_PENDING',
      });
    }

    iiafe(async () => {
      // The drawings are converted together, before their assets are saved. Those whose
      // conversion could not be requested are not added, and can be added again like other files
      // that failed.
      const failed = await AssetManager.instance.convertDrawingFiles(files);

      // Canceled and added again since: that attempt converts and adds the files.
      if (attempt !== saveAttempt.current) {
        return;
      }

      for (const file of failed) {
        dispatchItems({ type: 'SET_STATUS', item: file, status: ItemStatus.Failed });
      }

      startPromisePool();
    });
  };

  const cancelButtonClick = () => {
    dispatchItems({
      type: 'SET_ALL_PENDING_TO_CANCELED',
    });
  };

  function itemsReducer(state: ItemsState, action: ItemsAction) {
    switch (action.type) {
      case 'INIT_ITEMS_TO_PENDING':
        return action.items.map((x) => ({
          file: x,
          status: ItemStatus.Pending,
        }));

      case 'INIT_EXISTING_ITEMS_TO_PENDING':
        return state.map((x) =>
          [ItemStatus.Failed, ItemStatus.Canceled].includes(x.status)
            ? {
                ...x,
                status: ItemStatus.Pending,
              }
            : x,
        );

      case 'SET_STATUS':
        return state.map((x) =>
          x.file.id === action.item.id
            ? {
                ...x,
                status: action.status,
              }
            : x,
        );

      case 'SET_ALL_PENDING_TO_CANCELED':
        return state.map((x) =>
          x.status === ItemStatus.Pending
            ? {
                ...x,
                status: ItemStatus.Canceled,
              }
            : x,
        );
    }
  }

  const promiseGenerator = React.useCallback(() => {
    const item = items.find(
      (x: Item) =>
        x.status === ItemStatus.Pending && !itemIdsPickedByPromisePool.current.has(x.file.id),
    );

    // No more items?
    if (!item) {
      return null;
    }

    dispatchItems({
      type: 'SET_STATUS',
      item: item.file,
      status: ItemStatus.InProgress,
    });

    // Add current item to list of items already picked up by the promise generator.
    // This list is used to determining the next item to process.
    // Needed in order to get next item before next render has occurred (when item status is updated).
    itemIdsPickedByPromisePool.current.add(item.file.id);

    return new Promise<void>((resolve) => {
      iiafe(async () => {
        try {
          // Re-use old way to add a dynamic asset.
          // todo: Should be moved to API v1 in the future.
          if (getFileExtension(item.file.name).toLowerCase() === '.pdf') {
            await SkyMapAxiosServiceFactory.instance.createAssetServiceV1().createPdf({
              // Converts the PDF with line styles and curves.
              headers: FeatureFlagsManager.instance.isEnabled('SK-7535-design-curves')
                ? {
                    'x-flag': 'SK-7535-design-curves-and-linestyles' satisfies BackendFeatureFlags,
                  }
                : {},
              body: {
                fileId: item.file.id,
                timelineContainerId:
                  ProjectGeodataManager.instance().getActiveTimelineContainer()?.id,
                preferVector: pdfPreferVector,
              },
            });
          } else if (
            ['.jpg', '.jpeg'].includes(getFileExtension(item.file.name.toLowerCase())) &&
            createPois
          ) {
            await createPoiWithImage(item.file.name, item.file.id);
          } else {
            await AssetManager.instance.createDynamicAssetFromDocument(
              item.file.name,
              '', // Not relevant.
              item.file as FileModelV0, // As types in V0 and V1 is the same, this is ok.
              item.file.id,
            );
          }

          dispatchItems({
            type: 'SET_STATUS',
            item: item.file,
            status: ItemStatus.Success,
          });
        } catch (err) {
          dispatchItems({
            type: 'SET_STATUS',
            item: item.file,
            status: ItemStatus.Failed,
          });
        } finally {
          resolve();
        }
      });
    });
  }, [items, createPois, pdfPreferVector, createPoiWithImage]);

  const { start: startPromisePool } = usePromisePool(3, promiseGenerator);

  const renderInfoBox = () => {
    if (items.length === 0) {
      return (
        <InfoBox
          color="yellow"
          leftIcon={{
            icon: ['fad', 'info'],
          }}
        >
          {t('fileManager.addFilesToSkyViewDialog.info', { ns: 'components' })}
        </InfoBox>
      );
    }

    if (anyPendingOrInProgress) {
      return (
        <InfoBox
          color="yellow"
          leftIcon={{
            icon: ['fad', anyPendingOrInProgress ? 'spinner' : 'info'],
            spin: anyPendingOrInProgress,
          }}
        >
          {t('oneMoment', { ns: 'common' })}
        </InfoBox>
      );
    }
  };

  const renderErrorTable = () => {
    if (anyPendingOrInProgress || (!anyFailed && !anyCanceled)) {
      return;
    }

    return (
      <>
        {anyFailed && !anyCanceled && (
          <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
            <Trans
              components={{ bold: <strong /> }}
              i18nKey="fileManager.addFilesToSkyViewDialog.errorMessage"
              ns="components"
            />
          </InfoBox>
        )}

        {anyCanceled && (
          <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
            {t('fileManager.addFilesToSkyViewDialog.canceled', { ns: 'components' })}
          </InfoBox>
        )}

        <Table>
          <Table.Body>
            {items
              .filter((x) => [ItemStatus.Failed, ItemStatus.Canceled].includes(x.status))
              .map((x) => {
                const { icon, color } = filenameToFontAwesomeIcon(x.file.name);
                return (
                  <Table.Row key={x.file.id}>
                    <Table.Cell padding={0} shrink={true}>
                      <Icon
                        color={color}
                        data-testid="icon-file"
                        fixedWidth={true}
                        icon={['fad', icon]}
                      />
                    </Table.Cell>
                    <Table.Cell>{x.file.name}</Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      </>
    );
  };

  return (
    <Dialog width={350} onClose={() => props.onClose(true)}>
      {{
        header: t('fileManager.addFilesToSkyViewDialog.title', { ns: 'components' }),
        content: (
          <Stack direction="column" spacing={1}>
            {(hasPdf || hasJpg) && (
              <LabelledContainer text={t('options', { ns: 'common' })}>
                {hasPdf && (
                  <CheckBox
                    checked={pdfPreferVector}
                    onChange={() => setPdfPreferVector((oldValue) => !oldValue)}
                  >
                    {t('fileManager.addFilesToSkyViewDialog.pdfPreferVector', { ns: 'components' })}
                  </CheckBox>
                )}
                <br />
                {hasJpg && (
                  <CheckBox
                    checked={createPois}
                    onChange={() => setCreatePois((oldValue) => !oldValue)}
                  >
                    {t('fileManager.addFilesToSkyViewDialog.createPoi', { ns: 'components' })}
                  </CheckBox>
                )}
              </LabelledContainer>
            )}
            {renderInfoBox()}
            {renderErrorTable()}
          </Stack>
        ),
        footer: {
          right: (
            <>
              {anyPendingOrInProgress ? (
                <Button
                  color="primary"
                  disabled={anyCanceled || !anyPending}
                  leftIcon={anyCanceled ? { icon: ['fad', 'spinner'], spin: true } : undefined}
                  variant="contained"
                  onClick={cancelButtonClick}
                >
                  {t('cancel', { ns: 'common' })}
                </Button>
              ) : (
                <Button color="primary" variant="contained" onClick={saveButtonClick}>
                  {t('add', { ns: 'common' })}
                </Button>
              )}

              <Button disabled={anyPendingOrInProgress} variant="text" onClick={closeButtonClick}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

function fetchClosestTimelineContainer(project: Project, imageCapturedTime: number) {
  let closestTimeline: TimelineContainerModel | undefined;
  let closestTimeDifference = Number.MAX_SAFE_INTEGER;
  (project.timelineContainers ?? []).forEach((timeline) => {
    const currentDifference = Math.abs(
      imageCapturedTime - new Date(timeline.collectionDate).getTime(),
    );
    if (currentDifference < closestTimeDifference) {
      closestTimeline = timeline;
      closestTimeDifference = currentDifference;
    }
  });
  return closestTimeline;
}

export { AddFilesToSkyViewDialog, Props as AddFilesToSkyViewDialogProps };
